import React, { createContext, useContext, useState, useMemo, useCallback } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { CacheProvider } from '@emotion/react';
import createCache from '@emotion/cache';
import { lightTheme, darkTheme } from '../theme';

// Create a consistent Emotion cache for SSR hydration
// Using a specific key helps prevent hydration mismatches
const createEmotionCache = () => {
  return createCache({ key: 'mui', prepend: true });
};

// Create cache on module load to ensure consistency
const clientSideEmotionCache = createEmotionCache();

// Create Theme Context
const ThemeToggleContext = createContext({
  mode: 'light',
  toggleTheme: () => {},
});

// Custom hook to use theme toggle
export const useThemeToggle = () => useContext(ThemeToggleContext);

// Synchronous theme initialization - avoids useEffect delay for faster LCP
// Returns 'light' during SSR, reads localStorage on client
const getInitialTheme = () => {
  if (typeof window === 'undefined') {
    return 'light'; // SSR default
  }
  try {
    const savedTheme = localStorage.getItem('themeMode');
    if (savedTheme === 'dark' || savedTheme === 'light') {
      return savedTheme;
    }
  } catch {
    // localStorage might be unavailable in some contexts
  }
  return 'light';
};

export default function MuiThemeProvider({ children, emotionCache = clientSideEmotionCache }) {
  // Use lazy initializer to read theme synchronously on first render (client only)
  // This avoids the useEffect delay that causes LCP regression
  const [mode, setMode] = useState(getInitialTheme);

  // Toggle theme function - memoized to prevent unnecessary re-renders
  const toggleTheme = useCallback(() => {
    setMode(prevMode => {
      const newMode = prevMode === 'light' ? 'dark' : 'light';
      if (typeof window !== 'undefined') {
        try {
          localStorage.setItem('themeMode', newMode);
        } catch {
          // localStorage might be unavailable
        }
      }
      return newMode;
    });
  }, []);

  // Memoize the context value to prevent unnecessary re-renders
  const contextValue = useMemo(() => ({ mode, toggleTheme }), [mode, toggleTheme]);

  // Select the appropriate theme
  const theme = mode === 'light' ? lightTheme : darkTheme;

  return (
    <CacheProvider value={emotionCache}>
      <ThemeToggleContext.Provider value={contextValue}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          {children}
        </ThemeProvider>
      </ThemeToggleContext.Provider>
    </CacheProvider>
  );
}
